import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Camera, 
  Video, 
  X, 
  Upload, 
  AlertTriangle,
  CheckCircle,
  Loader2,
  FileImage,
  FileVideo,
  Info
} from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

const ISSUE_TYPES = [
  { key: "food_not_delivered", label: "Food Not Delivered", icon: "🚫", description: "Order marked delivered but food not received" },
  { key: "wrong_item", label: "Wrong Item Received", icon: "❌", description: "Received different items than ordered" },
  { key: "quality_issue", label: "Food Quality Issue", icon: "🍔", description: "Food quality not as expected" },
  { key: "rider_issue", label: "Problem with Rider", icon: "🛵", description: "Issues with delivery rider behavior" }
];

const DisputePage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [user, setUser] = useState(null);
  const [order, setOrder] = useState(null);
  const [canDispute, setCanDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Form state
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [description, setDescription] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]); // [{url, type, name}]
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  useEffect(() => {
    if (user && orderId) {
      fetchOrderAndCheckEligibility();
    }
  }, [user, orderId]);

  const fetchOrderAndCheckEligibility = async () => {
    try {
      setLoading(true);
      
      // Fetch order details
      const orderResponse = await axios.get(`${API}/orders/track/${orderId}`);
      setOrder(orderResponse.data);
      
      // Check if can dispute
      const canDisputeResponse = await axios.get(`${API}/orders/${orderId}/can-dispute`);
      setCanDispute(canDisputeResponse.data);
      
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    
    if (mediaFiles.length + files.length > 5) {
      setError("Maximum 5 files allowed");
      return;
    }
    
    for (const file of files) {
      // Validate file type
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      
      if (!isImage && !isVideo) {
        setError("Only images (JPEG, PNG, WEBP) and videos (MP4, MOV) are allowed");
        continue;
      }
      
      // Validate file size
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(`File too large. Max ${isVideo ? '50MB' : '10MB'} for ${isVideo ? 'videos' : 'images'}`);
        continue;
      }
      
      // Upload file
      await uploadFile(file);
    }
    
    // Clear input
    event.target.value = "";
  };

  const uploadFile = async (file) => {
    try {
      setUploadingFile(true);
      setError("");
      
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/upload/dispute-media`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.success) {
        setMediaFiles(prev => [...prev, {
          url: response.data.url,
          type: response.data.file_type,
          name: file.name,
          size: response.data.file_size
        }]);
      }
    } catch (error) {
      console.error("Upload error:", error);
      setError(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploadingFile(false);
    }
  };

  const removeFile = (index) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedIssue) {
      setError("Please select an issue type");
      return;
    }
    
    if (!description || description.trim().length < 10) {
      setError("Please provide a detailed description (at least 10 characters)");
      return;
    }
    
    if (mediaFiles.length === 0) {
      setError("Please upload at least one photo or video as evidence");
      return;
    }
    
    try {
      setSubmitting(true);
      setError("");
      
      const response = await axios.post(`${API}/disputes`, {
        order_id: orderId,
        user_id: user.id,
        issue_type: selectedIssue,
        description: description.trim(),
        media_urls: mediaFiles.map(f => f.url)
      });
      
      if (response.data.success) {
        setSuccess(true);
      }
    } catch (error) {
      console.error("Submit error:", error);
      setError(error.response?.data?.detail || "Failed to submit dispute");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
          <div className="max-w-lg mx-auto flex items-center gap-4">
            <button onClick={() => navigate("/orders")} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100">
              <ArrowLeft size={24} className="text-gray-700" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Dispute Submitted</h1>
          </div>
        </header>
        
        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle size={40} className="text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Dispute Submitted!</h2>
            <p className="text-gray-600 mb-6">
              Your dispute has been submitted and is under review. We will respond within 24-48 hours.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/orders")}
                className="w-full bg-[#3FA9F5] hover:bg-[#2196F3] text-white"
              >
                Back to My Orders
              </Button>
              <Button
                onClick={() => navigate("/home")}
                variant="outline"
                className="w-full"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Cannot dispute state
  if (canDispute && !canDispute.can_dispute) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
          <div className="max-w-lg mx-auto flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100">
              <ArrowLeft size={24} className="text-gray-700" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Raise Issue</h1>
          </div>
        </header>
        
        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle size={40} className="text-yellow-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Cannot Raise Dispute</h2>
            <p className="text-gray-600 mb-6">{canDispute.reason}</p>
            {canDispute.existing_dispute_id && (
              <p className="text-sm text-gray-500 mb-4">
                Existing dispute status: <span className="font-medium">{canDispute.existing_dispute_status}</span>
              </p>
            )}
            <Button
              onClick={() => navigate(-1)}
              className="bg-[#3FA9F5] hover:bg-[#2196F3] text-white"
            >
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100">
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Raise Issue</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Order Info Card */}
        {order && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[#3FA9F5] font-medium">Order #{order.order_number || "N/A"}</p>
                <h3 className="font-semibold text-gray-900">{order.restaurant_name}</h3>
                <p className="text-sm text-gray-500">₹{order.total}</p>
              </div>
              {canDispute?.hours_remaining && (
                <div className="text-right">
                  <p className="text-xs text-orange-500 font-medium">⏰ Time remaining</p>
                  <p className="text-sm text-gray-600">{canDispute.hours_remaining.toFixed(1)} hours</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Issue Type Selection */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-900 mb-4">What went wrong?</h3>
          <div className="space-y-3">
            {ISSUE_TYPES.map((issue) => (
              <button
                key={issue.key}
                onClick={() => setSelectedIssue(issue.key)}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  selectedIssue === issue.key
                    ? "border-[#3FA9F5] bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{issue.icon}</span>
                  <div>
                    <p className="font-medium text-gray-900">{issue.label}</p>
                    <p className="text-sm text-gray-500">{issue.description}</p>
                  </div>
                  {selectedIssue === issue.key && (
                    <CheckCircle size={20} className="text-[#3FA9F5] ml-auto" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-900 mb-2">Describe the issue</h3>
          <p className="text-sm text-gray-500 mb-3">Please provide details about what happened</p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Tell us what went wrong..."
            className="w-full h-32 p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#3FA9F5] focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1">{description.length}/500 characters</p>
        </div>

        {/* Media Upload */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-900 mb-2">Upload Evidence</h3>
          <p className="text-sm text-gray-500 mb-3">
            Photos or videos are required to process your dispute
          </p>
          
          {/* Upload Button */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFile || mediaFiles.length >= 5}
            className={`w-full p-4 border-2 border-dashed rounded-lg flex items-center justify-center gap-2 transition-colors ${
              uploadingFile 
                ? "border-gray-300 bg-gray-50 cursor-wait" 
                : mediaFiles.length >= 5
                ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                : "border-[#3FA9F5] hover:bg-blue-50 cursor-pointer"
            }`}
          >
            {uploadingFile ? (
              <>
                <Loader2 size={20} className="animate-spin text-[#3FA9F5]" />
                <span className="text-gray-600">Uploading...</span>
              </>
            ) : (
              <>
                <Upload size={20} className="text-[#3FA9F5]" />
                <span className="text-[#3FA9F5] font-medium">
                  {mediaFiles.length >= 5 ? "Maximum files reached" : "Upload Photos/Videos"}
                </span>
              </>
            )}
          </button>
          
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Camera size={12} /> Images: max 10MB
            </span>
            <span className="flex items-center gap-1">
              <Video size={12} /> Videos: max 50MB
            </span>
          </div>
          
          {/* Uploaded Files */}
          {mediaFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-gray-700">{mediaFiles.length}/5 files uploaded</p>
              {mediaFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                  {file.type === "image" ? (
                    <FileImage size={20} className="text-blue-500" />
                  ) : (
                    <FileVideo size={20} className="text-purple-500" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate">{file.name}</p>
                    <p className="text-xs text-gray-400">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">What happens next?</p>
              <ul className="list-disc list-inside space-y-1 text-blue-600">
                <li>Our team will review your dispute within 24-48 hours</li>
                <li>You'll receive a notification once resolved</li>
                <li>If approved, refund will be processed in 5-7 business days</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <div className="max-w-lg mx-auto">
          {/* Show what's missing */}
          {(!selectedIssue || description.length < 10 || mediaFiles.length === 0) && !submitting && (
            <div className="mb-2 text-xs text-center text-orange-500">
              {!selectedIssue && "⚠️ Select an issue type"}
              {selectedIssue && description.length < 10 && `⚠️ Description needs ${10 - description.length} more characters`}
              {selectedIssue && description.length >= 10 && mediaFiles.length === 0 && "⚠️ Upload at least one photo/video"}
            </div>
          )}
          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedIssue || description.length < 10 || mediaFiles.length === 0}
            className="w-full h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white disabled:bg-gray-300"
          >
            {submitting ? (
              <>
                <Loader2 size={20} className="animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              "Submit Dispute"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DisputePage;
